/**
 * A phone number is stored E.164 with the country code — +15735550142, never
 * 5735550142 — with the extension in its own column where the record has one,
 * and formatted for display (piece 10; decisions 0013 and 0340). A country code
 * nobody stored cannot be recovered later, so the store side refuses a number it
 * cannot place rather than guessing one. The three helpers here are the whole
 * rule: `parsePhone` takes a stored value apart for the editor, `toStoredPhone`
 * puts the editor's parts back together or names the problem, and
 * `formatPhone` is the one way a number is drawn.
 */
import { phoneDigits } from './normalise';
import { DEFAULT_PHONE_COUNTRY, NANP_COUNTRIES, PHONE_COUNTRIES, phoneCountry } from './countries';

export class PhoneNumberError extends Error {}

/** The calling code of every seeded country, by ISO code; kept for the callers that read it by name. */
export const CALLING_CODES: Readonly<Record<string, string>> = Object.fromEntries(PHONE_COUNTRIES.map((row) => [row.code, row.dialCode]));

/** A stored number taken apart for the editor. `country` is '' for a number whose code is outside the table, typed in full under Other. */
export interface ParsedPhone {
  country: string;
  /** The national digits; for an unplaced country, every digit after the plus. */
  national: string;
  extension: string;
}

/** The longest dial code the digits open with; +1 is the United States unless the caller prefers Canada. */
function placeDigits(digits: string, preferred: string | null): string | null {
  let best: { code: string; dial: string } | null = null;
  for (const row of PHONE_COUNTRIES) {
    if (!digits.startsWith(row.dialCode)) continue;
    if (best && row.dialCode.length < best.dial.length) continue;
    if (best && row.dialCode.length === best.dial.length && row.code !== preferred) continue;
    best = { code: row.code, dial: row.dialCode };
  }
  return best?.code ?? null;
}

/**
 * Take a stored value apart: the country its code names, the national digits
 * and the extension the caller holds beside it. A legacy value with no plus is
 * read as a North American number when it has ten digits, or eleven opening on
 * a one, and as the preferred country's national number otherwise.
 */
export function parsePhone(stored: string | null | undefined, extension?: string | null, preferredCountry: string = DEFAULT_PHONE_COUNTRY): ParsedPhone {
  const trimmed = (stored ?? '').trim();
  const ext = (extension ?? '').trim();
  if (!trimmed) return { country: preferredCountry, national: '', extension: ext };
  const digits = phoneDigits(trimmed);
  if (trimmed.startsWith('+')) {
    const code = placeDigits(digits, preferredCountry);
    if (!code) return { country: '', national: digits, extension: ext };
    return { country: code, national: digits.slice(phoneCountry(code)!.dialCode.length), extension: ext };
  }
  if (digits.length === 11 && digits.startsWith('1')) return { country: NANP_COUNTRIES.includes(preferredCountry) ? preferredCountry : 'US', national: digits.slice(1), extension: ext };
  return { country: preferredCountry, national: digits, extension: ext };
}

export type StoredPhone = { stored: string; problem: null } | { stored: null; problem: string };

/**
 * Put the editor's parts together as E.164, or say what is wrong. A country of
 * '' is the Other choice: the person typed the whole number, code first, and it
 * only has to be an E.164 length. A North American number typed with its
 * leading one loses it, because the one is the code and not the number.
 */
export function toStoredPhone(country: string, national: string): StoredPhone {
  let digits = phoneDigits(national);
  if (!digits) return { stored: null, problem: 'A phone number needs its digits.' };
  if (!country) {
    if (digits.length < 8 || digits.length > 15) return { stored: null, problem: 'A number typed with its own country code has 8 to 15 digits.' };
    return { stored: `+${digits}`, problem: null };
  }
  const row = phoneCountry(country);
  if (!row) return { stored: null, problem: `No country is seeded for ${country}; pick Other and type the code.` };
  if (row.dialCode === '1' && digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  const [min, max] = row.nationalLength;
  if (digits.length < min || digits.length > max) {
    return { stored: null, problem: min === max ? `A ${row.name} number has ${min} digits.` : `A ${row.name} number has ${min} to ${max} digits.` };
  }
  return { stored: `+${row.dialCode}${digits}`, problem: null };
}

/**
 * Store what a person typed. A number already in E.164 is kept; a national
 * number for a seeded country gains the code; anything else is refused, because
 * a stored number missing its country code is unrecoverable.
 */
export function toE164(typed: string, countryCode = DEFAULT_PHONE_COUNTRY): string {
  const trimmed = typed.trim();
  if (!trimmed) throw new PhoneNumberError('a phone number is stored with its country code');
  if (trimmed.startsWith('+')) {
    const digits = phoneDigits(trimmed);
    if (digits.length < 8 || digits.length > 15) throw new PhoneNumberError('an E.164 number carries 8 to 15 digits');
    return `+${digits}`;
  }
  const result = toStoredPhone(countryCode.toUpperCase(), trimmed);
  if (result.stored === null) throw new PhoneNumberError(result.problem);
  return result.stored;
}

/** The national digits in the groups a reader expects: 2-4-4 for ten, 3-3-3 for nine, 4-4 for eight, threes otherwise. */
function groupNational(digits: string): string {
  const shapes: Record<number, number[]> = { 5: [2, 3], 6: [3, 3], 7: [3, 4], 8: [4, 4], 9: [3, 3, 3], 10: [2, 4, 4], 11: [3, 4, 4], 12: [4, 4, 4] };
  const shape = shapes[digits.length];
  if (!shape) {
    if (digits.length <= 4) return digits;
    return (digits.match(/.{1,3}/g) ?? [digits]).join(' ');
  }
  const parts: string[] = [];
  let at = 0;
  for (const size of shape) {
    parts.push(digits.slice(at, at + size));
    at += size;
  }
  return parts.join(' ');
}

/**
 * Display: a North American number reads (573) 555-0142, a number in any
 * other seeded country reads +44 20 7946 0958, and one outside the table reads
 * as stored. An extension follows as ext. 12. Nothing else draws a number.
 */
export function formatPhone(stored: string | null | undefined, extension?: string | null): string {
  const trimmed = (stored ?? '').trim();
  if (!trimmed) return '';
  const ext = (extension ?? '').trim();
  const suffix = ext ? ` ext. ${ext}` : '';
  const parsed = parsePhone(trimmed);
  const row = phoneCountry(parsed.country);
  if (row && row.dialCode === '1' && parsed.national.length === 10) {
    const n = parsed.national;
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}${suffix}`;
  }
  if (row && trimmed.startsWith('+') && parsed.national) return `+${row.dialCode} ${groupNational(parsed.national)}${suffix}`;
  return `${trimmed}${suffix}`;
}
